/**
 * @description Head tags for person and company profile pages.
 *
 *              `canonicalPath` is the base profile URL, so the tab views
 *              (`/person/{name}/{id}#portfolio` and friends) all canonicalize back to the
 *              profile itself rather than minting a URL per tab.
 * @filename profile-meta.tsx
 */

import * as React from "react";
import { Helmet } from "react-helmet";
import { CANONICAL_ORIGIN, OG_IMAGE_URL } from "../../constants/site-urls";
import { ProfileMeta } from "../../utilities/profile-meta";

interface ProfileMetaTagsProps {
  /** Title, description and keywords built by `buildPersonMeta` / `buildCompanyMeta`. */
  meta: ProfileMeta;
  /** Base profile path, e.g. "/person/jane-doe/abc123". */
  canonicalPath: string;
  /** Headshot or logo to share; falls back to the site card when the profile has none. */
  imageUrl?: string;
}

export const ProfileMetaTags = (props: ProfileMetaTagsProps): React.ReactElement => {
  const { meta, canonicalPath, imageUrl } = props;
  const canonicalUrl = `${CANONICAL_ORIGIN}${canonicalPath}`;
  const shareImage = imageUrl || OG_IMAGE_URL;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta name="image" property="og:image" content={shareImage} />
      <meta property="og:type" content="profile" />
      <meta property="og:site_name" content="Radivision" />
    </Helmet>
  );
};

export default ProfileMetaTags;
